import type {
  FamilyPlanHistoryItem,
  FamilyPlanMemberView,
  FamilyPlanView,
} from '../types/family-plan-view.types';
import type { PaymentMethodSubject } from '../components/payment-method-modal/payment-method-modal.component';
import type { FamilyStartSubject } from '../types/family-start.types';
import { previewMemberPrice } from '../../components/notification-modal/utils/notification-family-sale.util';
import { familyTagsOf, type FamilyTags } from './family-tags.util';

/** One labelled value on a plate's card; the plate renders as a chip. */
export interface FamilyPlanField {
  label: string;
  value: string;
  chip?: boolean;
}

/** One plate's card, ready to bind. `fieldGroups` are the card's rows of labelled values. */
export interface FamilyPlanRow {
  key: string;
  subscriptionId: string;
  customerName: string;
  customerPhone: string;
  plate: string;
  plateState: string;
  vehicleDescription: string;
  packageName: string;
  /** `display_status` key for the status pill. */
  statusKey: 'active' | 'cancelled' | 'expired';
  tags: FamilyTags;
  fieldGroups: FamilyPlanField[][];
  history: FamilyPlanHistoryItem[];
  /** The member as the server sent it, for the actions that open on a card. */
  member: FamilyPlanMemberView;
  /**
   * A member with no phone of their own shares the primary's customer, so their name and phone
   * are the primary's and are not edited from their card.
   */
  contactLocked: boolean;
}

const EMPTY = '—';
const NO_WASHES = 'No washes yet';
const LIVE_STATUSES = new Set(['active', 'trialing']);

const dateLabel = (value: string | null): string => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : EMPTY;
};

const moneyLabel = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

/** "5%" or "$5.00"; a dash for a plate with no family discount (the primary). */
export function discountLabel(discount: FamilyPlanMemberView['membership']['discount']): string {
  if (!discount) {
    return EMPTY;
  }
  return discount.type === 'percentage' ? `${discount.value}%` : moneyLabel(discount.value);
}

/** What the plate pays each month: the list price less its family discount, if it has one. */
export function billedPrice(membership: FamilyPlanMemberView['membership']): number | null {
  const { price, discount } = membership;
  if (price === null || !discount) {
    return price;
  }
  const type = discount.type === 'flat' ? 'flat' : 'percentage';
  return previewMemberPrice(price, type, discount.value).recurringPrice;
}

/** "$47.00/mo" while the plate is billed; the bare price once it is not. */
export function priceLabel(membership: FamilyPlanMemberView['membership']): string {
  const billed = billedPrice(membership);
  if (billed === null) {
    return EMPTY;
  }
  const price = moneyLabel(billed);
  return membership.next_billing_date ? `${price}/mo` : price;
}

function statusKey(status: string | null): FamilyPlanRow['statusKey'] {
  const value = (status ?? '').toLowerCase();
  if (LIVE_STATUSES.has(value)) {
    return 'active';
  }
  return value === 'canceled' || value === 'cancelled' ? 'cancelled' : 'expired';
}

/**
 * The card's rows. The primary's card is the viewer's own plate in one row; a member's adds who
 * they are in a row above. A billed plate shows its next bill, an unbilled one its last wash.
 */
export function familyPlanFieldGroups(member: FamilyPlanMemberView): FamilyPlanField[][] {
  const { membership, customer, vehicle } = member;
  const plate: FamilyPlanField = {
    label: 'License plate',
    value: vehicle.license_plate ?? EMPTY,
    chip: true,
  };
  const billing: FamilyPlanField = membership.next_billing_date
    ? { label: 'Next bill', value: dateLabel(membership.next_billing_date) }
    : {
        label: 'Last wash',
        value: membership.last_washed_date ? dateLabel(membership.last_washed_date) : NO_WASHES,
      };
  const details: FamilyPlanField[] = [
    billing,
    { label: 'Added', value: dateLabel(membership.added_at) },
    { label: 'Discount', value: discountLabel(membership.discount) },
    { label: 'Price', value: priceLabel(membership) },
  ];
  if (member.role === 'primary') {
    return [[plate, ...details]];
  }
  return [
    [
      plate,
      { label: 'Customer name', value: customer.name ?? EMPTY },
      { label: 'Phone number', value: customer.phone ?? EMPTY },
    ],
    details,
  ];
}

/** "2022 Toyota Camry"; empty without a make or model, where a lone year reads like a date. */
export function vehicleDescription(vehicle: FamilyPlanMemberView['vehicle']): string {
  if (!vehicle.make && !vehicle.model) {
    return '';
  }
  return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
}

export function toFamilyPlanRow(
  member: FamilyPlanMemberView,
  primaryCustomerId: string | null = null
): FamilyPlanRow {
  return {
    key: member.member_id,
    subscriptionId: member.membership.subscription_id,
    customerName: member.customer.name ?? '',
    customerPhone: member.customer.phone ?? '',
    plate: member.vehicle.license_plate ?? '',
    plateState: member.vehicle.state ?? '',
    vehicleDescription: vehicleDescription(member.vehicle),
    packageName: member.membership.package_name ?? '',
    statusKey: statusKey(member.membership.status),
    tags: familyTagsOf(member.membership),
    fieldGroups: familyPlanFieldGroups(member),
    history: member.history,
    member,
    contactLocked: member.role === 'member' && member.customer.id === primaryCustomerId,
  };
}

export function familyPlanRows(view: FamilyPlanView | null): FamilyPlanRow[] {
  const members = view?.members ?? [];
  const primaryCustomerId =
    members.find(member => member.role === 'primary')?.customer.id ??
    view?.primary?.customer_id ??
    null;
  return members.map(member => toFamilyPlanRow(member, primaryCustomerId));
}

/** A member's tab shows the primary as a link and their own plate only. */
export function isSecondaryView(view: FamilyPlanView | null): boolean {
  return view?.in_family === true && view.viewer_role === 'member';
}

/** "2 of 5 plates • Harlem"; just the count while the limit is unknown, no shop when unnamed. */
export function seatsLabel(view: FamilyPlanView | null): string {
  const group = view?.group;
  if (!group) {
    return '';
  }
  const count =
    group.max_vehicles === null
      ? `${group.active_count} plates`
      : `${group.active_count} of ${group.max_vehicles} plates`;
  return group.location_name ? `${count} • ${group.location_name}` : count;
}

/** The Add vehicle button: only the primary's view, and only when the server says a seat is free. */
export function canAddVehicle(view: FamilyPlanView | null): boolean {
  return view?.viewer_role === 'primary' && view.group?.can_add === true;
}

/** The "Vehicle limit reached" note replaces the button when the primary's view has no seat left. */
export function isAtVehicleLimit(view: FamilyPlanView | null): boolean {
  return (
    view?.viewer_role === 'primary' &&
    view.group?.remaining_slots !== null &&
    view.group?.remaining_slots === 0
  );
}

/** What the add-members flow needs to open for this family; null when the view has no primary row. */
export function addVehicleSubject(view: FamilyPlanView | null): FamilyStartSubject | null {
  const primary = view?.members?.find(member => member.role === 'primary');
  if (!view?.group || !primary) {
    return null;
  }
  return {
    subscriptionId: primary.membership.subscription_id,
    plate: primary.vehicle.license_plate ?? '',
    packageName: primary.membership.package_name ?? '',
    state: primary.vehicle.state,
  };
}

/** What the Payment method pop-up shows for one plate of the family. */
export function familyPaymentSubject(member: FamilyPlanMemberView): PaymentMethodSubject {
  return {
    subscriptionId: member.membership.subscription_id,
    planName: member.membership.package_name ?? '',
    plate: (member.vehicle.license_plate ?? '').trim(),
    nextRenewal: member.membership.next_billing_date,
    price: billedPrice(member.membership),
  };
}

/** Update Payment Method is offered on a live plate once the shop has the feature on. */
export function canUpdatePaymentMethod(row: FamilyPlanRow, featureOn: boolean): boolean {
  return featureOn && row.statusKey === 'active';
}

/**
 * CWS-1579: Cancel membership is offered on a live plate once the shop has cancel & refund on.
 * A member viewing their own family (secondary view) cancels only their own plate, never the
 * primary's. Cancelling the primary opens the family flow (Cancel all / Cancel primary only).
 */
export function canCancelFamilyRow(
  view: FamilyPlanView | null,
  row: FamilyPlanRow,
  cancelOn: boolean
): boolean {
  if (!cancelOn || row.statusKey !== 'active') return false;
  return !isSecondaryView(view) || row.member.role !== 'primary';
}

/**
 * CWS-1579 (CWS-1582 §7): a live plate that shares the primary's customer can be moved to a number
 * of its own. Offered to the primary's view only: a member sees their own plate, not the others'.
 */
export function canMoveFamilyRow(view: FamilyPlanView | null, row: FamilyPlanRow): boolean {
  return row.contactLocked && row.statusKey === 'active' && !isSecondaryView(view);
}

/**
 * CWS-1579 (CWS-1582 §5): Update membership is offered on a live plate once the shop has plan
 * changes on, with the same reach as cancel: a member's view never changes the primary's plan.
 * The panel itself says when the plate cannot change (family plan off, a change pending, …).
 */
export function canChangeFamilyRowPlan(
  view: FamilyPlanView | null,
  row: FamilyPlanRow,
  planChangeOn: boolean
): boolean {
  return canCancelFamilyRow(view, row, planChangeOn);
}

/** The toast after a family plate's plan change: the same words the Customers tab shows. */
export function planChangeToast(
  notice: { title: string; body: string; upcoming: string | null } | null
): { title: string; detail: string } | null {
  if (!notice) return null;
  return { title: notice.title, detail: [notice.body, notice.upcoming].filter(Boolean).join(' ') };
}
